import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CheckCircle2, MapPin, ShieldCheck, Building2, Users, IndianRupee, Wallet } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { Badge } from "@/project/components/cards";
import { formatINR } from "@/project/data/sample";
import { getPartner, campaigns, programs, campaignPct, domainName, platformFee } from "@/project/data/network";

export const Route = createFileRoute("/organisations/$id")({
  loader: ({ params }: { params: any }) => {
    const org = getPartner(params.id);
    if (!org) throw notFound();
    return { org };
  },
  head: ({ loaderData }: { loaderData?: any }) => {
    if (!loaderData) return { meta: [{ title: "Organisation not found — Daan Amrut" }, { name: "robots", content: "noindex" }] };
    const { org } = loaderData;
    const title = `${org.name} — Daan Amrut Partner`;
    return {
      meta: [
        { title },
        { name: "description", content: org.about.slice(0, 155) },
        { property: "og:title", content: title },
        { property: "og:description", content: org.about.slice(0, 155) },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  errorComponent: () => <Missing />,
  notFoundComponent: () => <Missing />,
  component: OrgProfile,
});

function Missing() {
  return (
    <SiteShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Organisation not available</h1>
        <p className="mt-3 text-foreground/70">This partner profile may have been moved or is under review.</p>
        <Link to="/organisations" className="mt-6 inline-block rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)]">
          Back to directory
        </Link>
      </div>
    </SiteShell>
  );
}

function OrgProfile() {
  const { id } = Route.useParams();
  const org = getPartner(id)!;
  const orgCampaigns = campaigns.filter((c) => c.orgId === org.id);
  const orgPrograms = programs.filter((p) => p.orgId === org.id);

  return (
    <SiteShell>
      <div className="relative h-56 w-full overflow-hidden sm:h-80">
        <img src={org.cover} alt={org.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-4 pb-6 sm:px-6">
          <div className="flex flex-wrap gap-2">
            {org.verified && <Badge tone="verified"><CheckCircle2 className="h-3 w-3" /> Verified partner</Badge>}
            {org.has80G && <Badge tone="featured">80G eligible</Badge>}
            <Badge tone="neutral">{domainName(org.domain)}</Badge>
          </div>
          <h1 className="mt-3 font-display text-2xl font-extrabold text-white sm:text-4xl">{org.name}</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-white/80"><MapPin className="h-4 w-4" /> {org.city}, {org.state} · Partner since {org.joined}</p>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-8">
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat icon={IndianRupee} label="Raised on platform" value={formatINR(org.raised)} />
            <Stat icon={Users} label="Donors" value={org.donors.toLocaleString("en-IN")} />
            <Stat icon={Building2} label="Live campaigns" value={String(org.liveCampaigns)} />
            <Stat icon={ShieldCheck} label="Compliance score" value={`${org.complianceScore}/100`} />
          </div>

          <section>
            <h2 className="font-display text-xl font-bold">About this organisation</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground/75">{org.about}</p>
            {org.institutionId && (
              <Link to="/institutions/$id" params={{ id: org.institutionId }} className="mt-3 inline-block text-sm font-semibold text-[var(--maroon)]">
                View full shelter profile →
              </Link>
            )}
          </section>

          {orgCampaigns.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold">Campaigns by this organisation</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {orgCampaigns.map((c) => (
                  <Link key={c.id} to="/campaigns/$id" params={{ id: c.id }} className="overflow-hidden rounded-2xl border border-border/60 bg-card transition hover:shadow-[var(--shadow-card)]">
                    <img src={c.image} alt={c.title} loading="lazy" className="aspect-[16/9] w-full object-cover" />
                    <div className="p-4">
                      <p className="font-medium leading-snug">{c.title}</p>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
                        <div className="h-full" style={{ width: `${campaignPct(c)}%`, background: "var(--gradient-warm)" }} />
                      </div>
                      <p className="mt-2 text-xs text-foreground/65">{formatINR(c.raised)} of {formatINR(c.goal)} · {campaignPct(c)}%</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {orgPrograms.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold">Standard monthly giving</h2>
              <p className="mt-1 text-sm text-foreground/65">Always-on programmes you can support every month.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {orgPrograms.map((p) => (
                  <div key={p.id} className="rounded-2xl border border-border/60 bg-card p-5">
                    <p className="font-semibold">{p.title}</p>
                    <p className="mt-1 text-sm text-foreground/65">{p.impact}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-display text-lg font-bold text-[var(--maroon)]">{formatINR(p.monthly)}/mo</span>
                      <Link to="/donate" search={{ institution: org.institutionId }} className="rounded-full bg-[var(--maroon)] px-4 py-2 text-xs font-semibold text-[var(--cream)]">Start monthly</Link>
                    </div>
                    <p className="mt-2 text-xs text-foreground/70">{p.subscribers} monthly donors</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-border/60 bg-[var(--cream-2)] p-6">
            <h2 className="font-display text-lg font-bold">How funds reach this organisation</h2>
            <ol className="mt-3 space-y-2 text-sm text-foreground/75">
              <li>1. You donate through Daan Amrut and receive an instant 80G receipt.</li>
              <li>2. Funds are held in the foundation's designated account and reconciled daily.</li>
              <li>3. Settlement is released to the organisation's verified bank account twice a month.</li>
              <li>4. The organisation uploads expense proof and impact photos against every payout.</li>
            </ol>
            <p className="mt-3 text-xs text-foreground/60">{platformFee.note}</p>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-card)]">
            <p className="text-xs uppercase tracking-wider text-foreground/70">Support this organisation</p>
            <p className="mt-1 font-display text-xl font-bold">{org.name}</p>
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-[var(--cream-2)] p-3 text-xs text-foreground/70">
              <Wallet className="h-4 w-4" /> Pending settlement: {formatINR(org.payoutPending)}
            </div>
            <Link to="/donate" search={{ institution: org.institutionId }} className="mt-4 block rounded-full bg-[var(--maroon)] px-5 py-3 text-center text-sm font-semibold text-[var(--cream)]">
              Donate now
            </Link>
            <Link to="/campaigns" className="mt-2 block rounded-full border border-border px-5 py-3 text-center text-sm font-semibold">
              See all campaigns
            </Link>
          </div>
        </aside>
      </div>
    </SiteShell>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-4">
      <Icon className="h-4 w-4 text-[var(--saffron-deep)]" />
      <p className="mt-2 font-display text-lg font-bold">{value}</p>
      <p className="text-xs text-foreground/60">{label}</p>
    </div>
  );
}
